import { useState, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AppProvider, useApp } from './contexts/AppContext';
import { initAnalytics, trackPageView, trackSignupCompleted } from './lib/analytics';
import { initClarity } from './lib/clarity';

// ─────────────────────────────────────────────────────────────
// Google OAuth landing — runs SYNCHRONOUSLY at module load so it
// fires exactly once per page load, BEFORE React mounts. This
// avoids React.StrictMode's double-useEffect race that wipes the
// hash before the second invocation can read it, and is bulletproof
// against hot-reload, network races, focus events, etc.
//
// Backend lands us at `/login#token=<jwt>&user=<base64url-json>` on
// success. We persist token + user to localStorage, wipe the hash,
// and let AppContext's initial useEffect detect the localStorage and
// flip isAuthenticated → PublicRoute will then redirect us to the
// right dashboard on the very first render.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function __processOAuthLanding() {
  try {
    if (typeof window === 'undefined') return;
    const hash = (window.location.hash || '').replace(/^#/, '');
    if (!hash || !hash.includes('token=')) return;
    const params = new URLSearchParams(hash);
    const token = params.get('token');
    const userB64 = params.get('user');
    if (!token || !userB64) return;
    const pad = '='.repeat((4 - (userB64.length % 4)) % 4);
    const b64 = (userB64 + pad).replace(/-/g, '+').replace(/_/g, '/');
    // Robust UTF-8 base64url decode without using deprecated `unescape`.
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const userJson = new TextDecoder('utf-8').decode(bytes);
    const userObj = JSON.parse(userJson);
    localStorage.setItem('sip_token', token);
    localStorage.setItem('sip_user', JSON.stringify(userObj));
    localStorage.setItem('sip_school', JSON.stringify(null));
    // First-time Google signup? Defer firing GA4 conversion until React loads
    // analytics. We stash the flag in sessionStorage and AppRoutes picks it up.
    if (params.get('created') === '1') {
      try { sessionStorage.setItem('sip_oauth_signup', '1'); } catch { /* ignore */ }
    }
    window.history.replaceState({}, '', window.location.pathname + (window.location.search || ''));
    const role = userObj?.role;
    const dest = role === 'super_admin'
      ? '/super-admin/dashboard'
      : (userObj?.schoolId ? '/dashboard' : '/teacher-assignments');
    sessionStorage.setItem('sip_oauth_dest', dest);
  } catch (e) {
    try { sessionStorage.setItem('sip_oauth_error', 'google_failed'); } catch { /* ignore */ }
  }
}
__processOAuthLanding();

// Homepage pricing CTA (/signup?plan=pro, /login?plan=elite) → remember the plan
// so the in-app checkout opens right after signup / login.
(function __capturePendingPlan() {
  try {
    const plan = new URLSearchParams(window.location.search).get('plan');
    if (plan && ['starter', 'pro', 'elite'].includes(plan)) sessionStorage.setItem('ae_pending_plan', plan);
  } catch { /* ignore */ }
})();

// Initialise i18n once at module load so React subtree can use useTranslation().
import './i18n';

import { SplashScreen } from './components/SplashScreen';
import { MainLayout } from './components/layout/MainLayout';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/ForgotPasswordPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import {
  DashboardPage,
  LearningPage,
  StudentsPage,
  TeachersPage,
  OperationsPage,
  FinancePage,
  AnalyticsPage,
  LibraryPage,
  AdministrationPage,
  AttendancePage,
  ParentPortalPage,
  ClassAssignmentPage,
} from './pages';
import { SchoolManagementPage } from './pages/SchoolManagementPage';
import StudentProfilePage from './pages/StudentProfilePage';
import TeacherProfilePage from './pages/TeacherProfilePage';
import LeaveManagementPage from './pages/LeaveManagementPage';
import TeacherSchedulePage from './pages/TeacherSchedulePage';
import TeacherAssignmentsPage from './pages/TeacherAssignmentsPage';
import StudentAssignmentsPage from './pages/StudentAssignmentsPage';
import AiDebugLogsPage from './pages/AiDebugLogsPage';
import IntelligenceEnginePage from './pages/IntelligenceEnginePage';
import LandingPage from './pages/LandingPage';
import { TermsPage, PrivacyPage, TransparencyPage } from './pages/LegalPages';
import { PreferencesPage } from './pages/PreferencesPage';
// ─── Marketing site (multi-page public website) ───
import HomePage from './pages/marketing/HomePage';
import FeaturesPage from './pages/marketing/FeaturesPage';
import AIToolsForTeachersPage from './pages/marketing/AIToolsForTeachersPage';
import SchoolERPPage from './pages/marketing/SchoolERPPage';
import PricingPage from './pages/marketing/PricingPage';
import SchoolPilotProgramPage from './pages/marketing/SchoolPilotProgramPage';
import ResourcesPage from './pages/marketing/ResourcesPage';
import { BlogIndexPage, BlogCategoryPage, BlogPostPage } from './pages/marketing/BlogPages';
import SuperAdminBlogPage from './pages/SuperAdminBlogPage';
import SuperAdminAnalyticsPage from './pages/SuperAdminAnalyticsPage';
import SuperAdminGstPage from './pages/SuperAdminGstPage';
import BoscoStudioPage from './pages/BoscoStudioPage';
import BoscoCapturePage from './features/bosco-capture/BoscoCapturePage';
import BoscoCaptureUnderstandingView from './features/bosco-capture/BoscoCaptureUnderstandingView';
import BoscoCaptureKitPicker from './features/bosco-capture/BoscoCaptureKitPicker';
import BoscoCaptureKitProgress from './features/bosco-capture/BoscoCaptureKitProgress';
import BoscoCaptureLayout from './features/bosco-capture/BoscoCaptureLayout';
import InstallAppBanner from './features/pwa/InstallAppBanner';
import AboutPage from './pages/marketing/AboutPage';
import ContactPage from './pages/marketing/ContactPage';
import HelpCenterPage from './pages/marketing/HelpCenterPage';
import FaqPage from './pages/marketing/FaqPage';
import {
  PrivacyPolicyPage,
  TermsAndConditionsPage,
  RefundPolicyPage,
  ContentTakedownPolicyPage,
} from './pages/marketing/LegalPages';
import { LANDING_PAGES, makeSEOLandingComponent } from './pages/marketing/SEOLandingPages';
import './App.css';

// Protected Route component
function ProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, isLoading, currentUser } = useApp();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Individual (B2C) teachers — schoolId is null/falsy. They can ONLY access /teacher-assignments.
  // Use react-router's useLocation() instead of window.location.pathname — the latter lags
  // behind React Router's internal state during back-to-back <Navigate> redirects, causing
  // a stuck render where the page is left blank.
  const isIndividual = currentUser?.role === 'teacher' && !currentUser?.schoolId;
  const INDIVIDUAL_ALLOWED = ['/teacher-assignments', '/preferences', '/my-profile', '/bosco-studio', '/bosco-capture', '/library'];
  const isAllowedPath = INDIVIDUAL_ALLOWED.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));
  if (isIndividual && !isAllowedPath) {
    return <Navigate to="/teacher-assignments" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(currentUser?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Public Route (redirect if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading, currentUser } = useApp();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Individual teachers (no schoolId) live on /teacher-assignments — never the
    // institutional dashboard. Send them straight there to avoid the flash-of-dashboard.
    const isIndividualTeacher = currentUser?.role === 'teacher' && !currentUser?.schoolId;
    const target = currentUser?.role === 'parent'
      ? '/parent-portal'
      : isIndividualTeacher
        ? '/teacher-assignments'
        : '/dashboard';
    return <Navigate to={target} replace />;
  }

  return children;
}

// Root gate: at exact "/", show LandingPage when unauthenticated, otherwise
// fall through to the protected app shell. Child routes only render for
// authenticated users (since LandingPage doesn't include an <Outlet/>).
//
// Individual (B2C) teachers — identified by role='teacher' AND no schoolId —
// are LOCKED to /teacher-assignments. No dashboard, no students list, no
// administration, no super-admin pages, no landing page, no deep-linked
// attempts. This is enforced at the RootGate level because many child routes
// deliberately skip ProtectedRoute (e.g. /dashboard, /learning, /students).
function RootGate({ children }) {
  const { isAuthenticated, isLoading, currentUser } = useApp();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Unauthenticated visitors at the literal root see the marketing home.
  if (!isAuthenticated && location.pathname === '/') {
    return <HomePage />;
  }
  // For any other path while unauthenticated → standard protected redirect to /login.
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  // 🔒 B2C individual teacher lockdown — never let them off /teacher-assignments.
  // Applies to /dashboard, /, /students, /learning, /administration, /super-admin/*,
  // everything. Does NOT affect school teachers (they have schoolId set).
  // EXCEPTION: allow them to manage their account at /preferences and /my-profile.
  const isIndividual = currentUser?.role === 'teacher' && !currentUser?.schoolId;
  const INDIVIDUAL_ALLOWED = ['/teacher-assignments', '/preferences', '/my-profile', '/bosco-studio', '/bosco-capture', '/library'];
  const isAllowedPath = INDIVIDUAL_ALLOWED.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));
  if (isIndividual && !isAllowedPath) {
    return <Navigate to="/teacher-assignments" replace />;
  }
  // Authenticated → render the protected app shell (children = MainLayout w/ Outlet).
  return children;
}

function AppRoutes() {
  const [showSplash, setShowSplash] = useState(true);
  const { isAuthenticated } = useApp();
  const location = useLocation();

  // ─── Google Analytics 4 / Ads (G-NYVWMWGHNF) ───
  // Initialise once on first render; emit a page_view on every route change.
  // No-ops on preview/localhost and when DNT is enabled — see lib/analytics.js.
  useEffect(() => {
    initAnalytics();
    // Microsoft Clarity (production-only — see lib/clarity.js)
    initClarity();
    // Fire signup_completed for first-time Google OAuth users (flag set in
    // the synchronous landing handler at the top of this file).
    try {
      if (sessionStorage.getItem('sip_oauth_signup') === '1') {
        sessionStorage.removeItem('sip_oauth_signup');
        trackSignupCompleted({ method: 'google', plan: 'free' });
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { trackPageView(location.pathname + location.search); }, [location.pathname, location.search]);

  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
  }, []);

  // 🔐 OAuth-return short-circuit: the module-level Google handler at the
  // top of App.js stashes the destination in sessionStorage and wipes the
  // hash before React renders. Skip splash whenever we see that token OR
  // an error= param so LoginPage's redirect/banner fires instantly.
  const url = typeof window !== 'undefined' ? window.location : null;
  const hasOAuthReturn = !!url && (
    !!sessionStorage.getItem('sip_oauth_dest') ||
    !!sessionStorage.getItem('sip_oauth_error') ||
    /[?&]error=/.test(url.search || '')
  );

  // Only show splash on initial load when not authenticated AND not mid-OAuth.
  if (showSplash && !isAuthenticated && !hasOAuthReturn) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email"
        element={<VerifyEmailPage />}
      />

      {/* Public legal pages — must be reachable WITHOUT auth, so Google's
          OAuth verification reviewers (and any logged-out visitor) can
          read them. */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/legal/transparency" element={<TransparencyPage />} />

      {/* ─── Public marketing site (multi-page website) ───
          Reachable to both logged-in and logged-out visitors. Order matters:
          these must be declared BEFORE the catch-all RootGate at "/". */}
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/ai-tools-for-teachers" element={<AIToolsForTeachersPage />} />
      <Route path="/school-erp" element={<SchoolERPPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/school-pilot-program" element={<SchoolPilotProgramPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/category/:slug" element={<BlogCategoryPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/help-center" element={<HelpCenterPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/content-takedown-policy" element={<ContentTakedownPolicyPage />} />

      {/* ─── SEO landing pages (keyword-targeted) ───
          Generated from LANDING_PAGES map in /pages/marketing/SEOLandingPages.jsx.
          Add a new keyword cluster page = add a key to that map (no JSX edit). */}
      {Object.keys(LANDING_PAGES).map((slug) => {
        const Comp = makeSEOLandingComponent(slug);
        return <Route key={slug} path={`/${slug}`} element={<Comp />} />;
      })}

      {/* Legacy landing kept under a stable URL for backwards compat. */}
      <Route path="/landing-classic" element={<LandingPage />} />

      {/* BOSCO Studio — top-level fullscreen terminal (no MainLayout chrome) */}
      <Route
        path="/bosco-studio"
        element={
          <ProtectedRoute>
            <BoscoStudioPage />
          </ProtectedRoute>
        }
      />

      {/* BOSCO Capture — mobile-first camera flow (no MainLayout chrome).
          Wrapped in BoscoCaptureLayout so all child screens share a single
          Sonner <Toaster/> instance — otherwise toast.error/toast.success
          calls silently drop (see iter 85 test report). */}
      <Route
        element={
          <ProtectedRoute>
            <BoscoCaptureLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/bosco-capture" element={<BoscoCapturePage />} />
        <Route path="/bosco-capture/understanding/:sessionId" element={<BoscoCaptureUnderstandingView />} />
        <Route path="/bosco-capture/kit/:sessionId" element={<BoscoCaptureKitPicker />} />
        <Route path="/bosco-capture/kit/progress/:kitId" element={<BoscoCaptureKitProgress />} />
      </Route>

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <RootGate>
            <MainLayout />
          </RootGate>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route
          path="attendance"
          element={
            <ProtectedRoute allowedRoles={['admin', 'teacher']}>
              <AttendancePage />
            </ProtectedRoute>
          }
        />
        <Route path="learning" element={<LearningPage />} />
        <Route path="students" element={<StudentsPage />} />
        <Route path="students/:studentId" element={<StudentProfilePage />} />
        <Route
          path="teachers"
          element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
              <TeachersPage />
            </ProtectedRoute>
          }
        />
        <Route path="teachers/:teacherId" element={<TeacherProfilePage />} />
        <Route path="my-profile" element={<TeacherProfilePage />} />
        <Route path="preferences" element={<PreferencesPage />} />
        <Route path="leave-management" element={<LeaveManagementPage />} />
        <Route path="teacher-schedule" element={<TeacherSchedulePage />} />
        <Route
          path="teacher-assignments"
          element={
            <ProtectedRoute allowedRoles={['teacher']}>
              <TeacherAssignmentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="student-assignments"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentAssignmentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="operations"
          element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
              <OperationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="finance"
          element={
            <ProtectedRoute allowedRoles={['admin', 'finance_admin', 'super_admin']}>
              <FinancePage />
            </ProtectedRoute>
          }
        />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="library" element={<LibraryPage />} />
        <Route
          path="administration"
          element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
              <AdministrationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="school-management"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SchoolManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="super-admin/ai-debug-logs"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <AiDebugLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="super-admin/intelligence"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <IntelligenceEnginePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="super-admin/blog"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminBlogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="super-admin/gst-revenue"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminGstPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="super-admin/analytics"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminAnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="parent-portal"
          element={
            <ProtectedRoute allowedRoles={['parent']}>
              <ParentPortalPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="class-assignments"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ClassAssignmentPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AppProvider>
        <BrowserRouter>
          <AppRoutes />
          {/* Global PWA install banner — self-hides when already installed
              or previously dismissed within the last 7 days. Rendered
              outside <Routes> so it survives navigation. */}
          <InstallAppBanner />
        </BrowserRouter>
      </AppProvider>
    </HelmetProvider>
  );
}

export default App;
